import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Column from "../../Charts/Column";
import { ChartTitle } from "../styles/employer-analytics-styles";
import { Wrapper, Chart } from "../styles/total-jobs-posted-styles";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";

interface ITotalJobsPostedProps {
  totalJobsPosted: {
    values?: Array<number>;
    months?: Array<string>;
  };
  pdfExport?: boolean;
  spendPerSectorPdfExportSelected?: boolean;
  totalJobsPostedWidgetLoader: boolean;
}

export const TotalJobsPosted: FunctionComponent<ITotalJobsPostedProps> = ({
  totalJobsPosted,
  pdfExport,
  spendPerSectorPdfExportSelected,
  totalJobsPostedWidgetLoader,
}) => {
  const totalJobsPostedOptions = {
    series: [
      {
        data: totalJobsPosted.values,
      },
    ],
    options: {
      chart: {
        height: 280,
        type: "bar",
        offsetY: 10,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => Math.ceil(val - 0.2),
        offsetY: -8,
        style: {
          fontSize: "10px",
          colors: ["#304758"],
        },
      },
      tooltip: {
        enabled: false,
      },
      xaxis: {
        categories: totalJobsPosted.months,
        position: "top",
        labels: {
          offsetY: -6,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      fill: {
        gradient: {
          shade: "light",
          type: "horizontal",
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [50, 0, 100, 100],
        },
      },
      yaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: false,
        },
      },
      title: {
        text: pdfExport ? "" : "Total jobs posted in the past 12 months",
        floating: true,
        offsetY: 260,
        align: "center",
        style: {
          color: "#333",
          fontSize: "14.4px",
          fontWeight: "light",
          fontFamily: "Komet, sans-serif",
        },
      },
      responsive: [
        {
          breakpoint: 450,
          options: {
            xaxis: {
              labels: {
                offsetY: -3,
              },
            },
            title: {
              offsetY: 262,
            },
          },
        },
      ],
    },
  };

  return (
    <Wrapper
      className="animated fadeIn delay-6"
      pdfExport={pdfExport}
      spendPerSectorPdfExportSelected={spendPerSectorPdfExportSelected}
    >
      <AnalyticsWidgetLoading
        text="Updating..."
        active={totalJobsPostedWidgetLoader}
      />
      <ChartTitle
        data-test-id="total-jobs-posted-chart-title"
        marginBottom={10}
      >
        Total Jobs Posted (12 month view)
      </ChartTitle>
      <Chart>
        <Column
          graphOptions={totalJobsPostedOptions}
          dataTestId="total-jobs-posted-chart"
        />
      </Chart>
    </Wrapper>
  );
};

TotalJobsPosted.propTypes = {
  totalJobsPosted: PropTypes.shape({
    months: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number),
  }),
  pdfExport: PropTypes.bool,
  spendPerSectorPdfExportSelected: PropTypes.bool,
  totalJobsPostedWidgetLoader: PropTypes.bool.isRequired,
};

TotalJobsPosted.defaultProps = {
  pdfExport: null,
  spendPerSectorPdfExportSelected: null,
  totalJobsPosted: {
    months: [],
    values: [],
  },
};

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      totalJobsPosted: unknown;
    };
    totalJobsPostedWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  totalJobsPosted: state.employerAnalytics.chartsData.totalJobsPosted,
  totalJobsPostedWidgetLoader:
    state.employerAnalytics.totalJobsPostedWidgetLoader,
});

export default connect(mapStateToProps, {})(TotalJobsPosted);
